/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { Descriptions, Tabs, Button, message, Tooltip } from 'antd';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import Icon from 'hyarchis-global/components/icon';
import styled from 'styled-components';
import { JobCrossMatch, ProcessingJob } from 'types/proccesingJobs';
import moment from 'moment';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'app-constants';
import Collapsible from 'react-collapsible';
import downloadFile from './downloadFile';
import ColorPickerService from '../../../services/helpers/colorPickerService';

const { TabPane } = Tabs;

interface OwnProps {
  job: ProcessingJob;
  crossMatches: JobCrossMatch[];
}

const StyledWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-top: 0px;
  padding: 10px 16px;
`;

const CollapseTogglerWrapper = styled.div`
  width: 100%;
`;

const StyledDescriptionsItem = styled(Descriptions.Item)`
  min-width: 180px;
`;

const StyledDescriptions = styled(Descriptions)`
  margin-top: 16px;
`;

const StyledPaddingDisplay = styled.div`
  padding-bottom: 6px;
`;

const StyledPiiDataItem = styled.div`
  display: flex;
`;
const StyledPiiDataKey = styled.div`
  width: 50%;
  font-weight: bold;
`;
const StyledPiiDataValue = styled.div`
  width: 50%;
`;

const StyledTabs = styled(Tabs)`
  margin-top: 12px;
`;

const StyledDownloadButton = styled(Button)`
  margin-left: 12px;
`;

const StyledDataItem = styled.div`
  display: flex;
  font-weight: normal;
`;

const StyledDataKey = styled.div`
  width: 20%;
  font-weight: bold;
`;

const StyledDataValue = styled.div`
  width: 60%;
  font-weight: normal;
`;

const StyledJsonValue = styled.div`
  width: 100%;
  font-weight: normal;
`;

const StyledScrollableRiskAssessment = styled.div<{ scrollable: boolean }>`
  overflow-y: ${(props) => (props.scrollable ? 'scroll;' : 'hidden;')}
  height : ${(props) => (props.scrollable ? '300px;' : 'auto;')}
`;

const StyledCsvDownloadButton = styled.div`
  padding-bottom: 5px;
`;

const JobListItemBody = ({ job, crossMatches }: OwnProps) => {
  const { output } = job;

  const [collapsableOpen, setCollapsableOpen] = useState(false);

  const documentTypeValue = output.documentType ? output.documentType.documentType : '—';
  const idExistanceInPages =
    output.idExistanceInPages && output.idExistanceInPages.length > 0
      ? output.idExistanceInPages.join(', ')
      : '—';
  const documentDataPoints = output.documentDataPoints || [];
  const risks = output.risks || [];

  const onCollapseClick = () => {
    const newCollapsableValue = !collapsableOpen;
    setCollapsableOpen(newCollapsableValue);
    if (job && newCollapsableValue) {
      navigator.clipboard.writeText(job.fullOutputJson);
      message.success('Copied to clipboard.', 1);
    }
  };

  return (
    <StyledWrapper>
      <Descriptions title='Document data' bordered column={1} size='small'>
        <Descriptions.Item label='WorkflowId'>{job.workflowId}</Descriptions.Item>
        <Descriptions.Item label='Classified Document Type'>{documentTypeValue}</Descriptions.Item>
        {output.documentType &&
          output.documentType.subDocumentType &&
          output.documentType.subDocumentType !== 'Unknown' && (
            <Descriptions.Item label='Classified Document Type - Sub type'>
              {output.documentType.subDocumentType}
            </Descriptions.Item>
          )}
        <Descriptions.Item label='Id/Passport can be found at pages'>
          {idExistanceInPages}
        </Descriptions.Item>
        {documentDataPoints.map((dataPoint, dataPointIndex) => (
          <Descriptions.Item label={dataPoint.type} key={`document-datapoint-${dataPointIndex}`}>
            {dataPoint.value}
          </Descriptions.Item>
        ))}
        {output.faceVerification && (
          <Descriptions.Item label='ID/Passport verification'>
            {output.faceVerification}
          </Descriptions.Item>
        )}
      </Descriptions>
      <StyledTabs defaultActiveKey='page-0'>
        {output.perPage &&
          output.perPage.map((pageOutput, pageIndex) => (
            <TabPane tab={`Page ${pageOutput.pageNumber}`} key={`page-${pageIndex}`}>
              <StyledDescriptions bordered column={1} size='small'>
                {pageOutput.piis.map((pii, piiIndex) => (
                  <Descriptions.Item
                    label={`ID/Passport #${piiIndex + 1}`}
                    key={`page-${pageIndex}-pii-${piiIndex}`}
                  >
                    {pii.firstName && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>First name</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.firstName}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.lastName && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Last name</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.lastName}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.nationality.name && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Nationality</StyledPiiDataKey>
                        <StyledPiiDataValue>
                          {pii.nationality ? pii.nationality.name : '—'}
                        </StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.documentNumber && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Document number</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.documentNumber}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.personalNumber && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Personal number (BSN)</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.personalNumber}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.gender && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Gender</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.gender}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.dateOfBirth && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Date of birth</StyledPiiDataKey>
                        <StyledPiiDataValue>
                          {moment(pii.dateOfBirth).format(DATE_FORMAT)}
                        </StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.issuingCountry.name && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Issuing country</StyledPiiDataKey>
                        <StyledPiiDataValue>
                          {pii.issuingCountry ? pii.issuingCountry.name : '—'}
                        </StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.expirationDate && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Expiration</StyledPiiDataKey>
                        <StyledPiiDataValue>
                          {moment(pii.expirationDate).format(DATE_FORMAT)}
                        </StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.documentType && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Document type</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.documentType}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.documentSubType && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Document sub type</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.documentType}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.residencePermitStartDate && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Residence permission start date</StyledPiiDataKey>
                        <StyledPiiDataValue>
                          {moment(pii.residencePermitStartDate).format(DATE_FORMAT)}
                        </StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.issuingAuthority && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Issuing authority</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.issuingAuthority}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.placeOfBirth && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Place of birth</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.placeOfBirth}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.mrz && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>MRZ</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.mrz}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.fullFirstName && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Full first name</StyledPiiDataKey>
                        <StyledPiiDataValue>{pii.fullFirstName}</StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                    {pii.dateOfIssue && (
                      <StyledPiiDataItem>
                        <StyledPiiDataKey>Date of issue</StyledPiiDataKey>
                        <StyledPiiDataValue>
                          {moment(pii.dateOfIssue).format(DATE_FORMAT)}
                        </StyledPiiDataValue>
                      </StyledPiiDataItem>
                    )}
                  </Descriptions.Item>
                ))}
                {pageOutput.dataPoints.map((dataPoint, dataPointIndex) => {
                  const color = ColorPickerService.getSingleBorderColor(dataPoint, crossMatches);
                  return (
                    <Descriptions.Item
                      style={{
                        color,
                      }}
                      label={dataPoint.type}
                      key={`page-${pageIndex}-datapoint-${dataPointIndex}-${color}`}
                    >
                      {dataPoint.value}
                    </Descriptions.Item>
                  );
                })}
                {pageOutput.groupedDataPoints &&
                  pageOutput.groupedDataPoints.map((groupedDataPoint, groupIndex) => (
                    <StyledDescriptionsItem
                      label={`Group #${groupIndex + 1}`}
                      style={{ minWidth: '165px' }}
                      key={`page-${pageIndex}-group-${groupIndex}`}
                    >
                      {groupedDataPoint &&
                        groupedDataPoint.map((dataPoint, dataPointIndex) => (
                          <StyledDataItem
                            key={`page-${pageIndex}-group-${groupIndex}-datapoint-${dataPointIndex}`}
                          >
                            <StyledDataKey>{dataPoint.type}</StyledDataKey>
                            <StyledDataValue>{dataPoint.value}</StyledDataValue>
                          </StyledDataItem>
                        ))}
                    </StyledDescriptionsItem>
                  ))}
                {pageOutput.idImages.length > 0 && (
                  <Descriptions.Item label='ID cut-outs'>
                    {pageOutput.idImages.map((idImage, idImageIndex) => (
                      <div
                        key={`page-${pageIndex}-idImage-${idImageIndex}`}
                        style={{ marginBottom: '5px' }}
                      >
                        <span>
                          {idImage.type}
                          {idImage.subType ? ` (${idImage.subType})` : ''} {idImageIndex + 1}
                        </span>
                        <Tooltip title='Download as PDF'>
                          <StyledDownloadButton
                            onClick={downloadFile(
                              idImage.fileId || '',
                              `${job.createdAt}--${job.processingJobId}--page${pageIndex + 1}--pdf${
                                idImageIndex + 1
                              }.pdf`,
                            )}
                            size='small'
                            shape='circle'
                            icon={<FilePdfOutlined />}
                          />
                        </Tooltip>
                      </div>
                    ))}
                  </Descriptions.Item>
                )}
              </StyledDescriptions>
            </TabPane>
          ))}
      </StyledTabs>
      {risks && risks.length > 0 && (
        <StyledDescriptions bordered column={1} size='small'>
          {risks.map((risk, riskIndex) => (
            <StyledDescriptionsItem
              label={`Risk Assessment #${riskIndex + 1}`}
              style={{ minWidth: '165px' }}
            >
              <StyledDataItem>
                <StyledDataKey>Name</StyledDataKey>
                <StyledDataValue>{risk.personFullName}</StyledDataValue>
              </StyledDataItem>
              <StyledDataItem>
                <StyledDataKey>Risk level</StyledDataKey>
                <StyledDataValue>{risk.personRiskLevel}</StyledDataValue>
              </StyledDataItem>
              <StyledDataItem>
                <StyledDataKey>Searched At</StyledDataKey>
                <StyledDataValue>
                  {moment(risk.searchPerformedAt).format(DATE_TIME_FORMAT)}
                </StyledDataValue>
              </StyledDataItem>
              {risk.riskAssessments && (
                <Descriptions.Item>
                  <StyledDataItem>
                    {job.output.fileId && (
                      <Descriptions.Item>
                        <StyledDataKey>Data file</StyledDataKey>
                        <StyledDataValue>
                          <StyledCsvDownloadButton>
                            <Tooltip title='Download as CSV'>
                              <StyledDownloadButton
                                onClick={downloadFile(output.fileId || '', `${output.fileId}.csv`)}
                                style={{ margin: '0px' }}
                                size='small'
                                shape='circle'
                                icon={<DownloadOutlined />}
                              />
                            </Tooltip>
                          </StyledCsvDownloadButton>
                        </StyledDataValue>
                      </Descriptions.Item>
                    )}
                  </StyledDataItem>
                  <StyledScrollableRiskAssessment scrollable={risk.riskAssessments.length > 1}>
                    {risk.riskAssessments.map((riskKeyword, riskKeywordIndex) => (
                      <StyledDataItem>
                        <StyledDataKey>Risky Search Result #{riskKeywordIndex + 1}</StyledDataKey>
                        <StyledDataValue>
                          <StyledPaddingDisplay>
                            <b>
                              {riskKeyword.keywords
                                .map((riskWord) => riskWord.toLowerCase())
                                .join(' | ')}
                            </b>
                          </StyledPaddingDisplay>
                          <StyledPaddingDisplay>{riskKeyword.searchTitle}:</StyledPaddingDisplay>
                          <StyledPaddingDisplay>
                            <a href={riskKeyword.searchUrl} target='_blank' rel='noreferrer'>
                              {riskKeyword.searchUrl}
                            </a>
                          </StyledPaddingDisplay>
                          <StyledPaddingDisplay>
                            {riskKeyword.searchDescription}
                          </StyledPaddingDisplay>
                        </StyledDataValue>
                      </StyledDataItem>
                    ))}
                  </StyledScrollableRiskAssessment>
                </Descriptions.Item>
              )}
            </StyledDescriptionsItem>
          ))}
        </StyledDescriptions>
      )}
      <Collapsible trigger='' open={job?.fullOutputJson === undefined}>
        <StyledDataValue>{job?.fullOutputJson}</StyledDataValue>
      </Collapsible>
      <CollapseTogglerWrapper>
        <Button
          icon={
            <Icon icon={collapsableOpen ? 'chevronUp' : 'chevronDown'} type='regular' size={24} />
          }
          size='large'
          type='text'
          onClick={(e) => onCollapseClick()}
        >
          Job output
        </Button>
      </CollapseTogglerWrapper>
      <Collapsible trigger='' open={collapsableOpen}>
        <StyledJsonValue>{job?.fullOutputJson}</StyledJsonValue>
      </Collapsible>
    </StyledWrapper>
  );
};

export default React.memo(JobListItemBody);
